import { useEffect, useState } from "react"

import { useCommonProductsList } from "@boundless/bl-api-client/common/services"
import {
  getCustomerApplicationsRetrieveQueryKey,
  getCustomerCompanyRetrieveQueryKey,
  getCustomerFundingNeedsRetrieveQueryKey,
  getCustomerLendersRetrieveQueryKey,
  useCustomerApplicationsSelectLenderCreate,
  useCustomerFundingNeedsRetrieveSuspense,
  useCustomerLendersAvailableList,
} from "@boundless/bl-api-client/customer/services"
import { zodResolver } from "@hookform/resolvers/zod"
import { useQueryClient } from "@tanstack/react-query"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { ChevronDown } from "lucide-react"
import { useForm } from "react-hook-form"
import { z } from "zod"

import { ButtonBack } from "@/components/buttons/ButtonBack"
import { ButtonNext } from "@/components/buttons/ButtonNext"
import { FormHeader } from "@/components/form-elements/FormHeader"
import { FormInputNumeric } from "@/components/form-elements/FormInputNumeric"
import FormMultiselect from "@/components/form-elements/FormMultiselect"
import { FormSection } from "@/components/form-elements/FormSection"
import { BookACallCard } from "@/components/lender-match/BookACallCard"
import { LenderMatchForm } from "@/components/lender-match/LenderMatchForm"
import { Card } from "@/components/ui/card"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import { Form } from "@/components/ui/form"
import type { ErrorType } from "@/lib/clients"
import { ApplicationFlowContext } from "@/lib/flows/application"
import { applyErrorsToForm, type ErrorResponseType } from "@/lib/forms/utils"
import { applicationSelectLenderFormSchema } from "@/lib/schemas"

import type { Product } from "@boundless/bl-api-client/common/models/Product"
import type { LenderProfile } from "@boundless/bl-api-client/customer/models"

export const Route = createFileRoute(
  "/_dashboard/_application-flow/funding-requests/$fundingNeedsUuid/lender-match",
)({
  component: FundingRequestLenderMatch,
  beforeLoad: ({ context: { store } }) => {
    const companyUuid = store.getState().activeCompanyUuid
    if (!companyUuid) {
      throw redirect({ to: "/home" })
    }
    return { companyUuid }
  },
})

function FundingRequestLenderMatch() {
  const { fundingNeedsUuid } = Route.useParams()
  const { companyUuid } = Route.useRouteContext()

  const queryClient = useQueryClient()
  const flowActorRef = ApplicationFlowContext.useActorRef()

  const [filtersOpen, setFiltersOpen] = useState(false)

  const filterForm = useForm({
    defaultValues: {
      capitalAmount: undefined as number | undefined,
      productOptions: [] as Record<string, string>[],
    },
  })

  const { data: fundingNeed } = useCustomerFundingNeedsRetrieveSuspense(
    companyUuid,
    fundingNeedsUuid,
  )
  const application = fundingNeed.unsubmittedApplication

  const form = useForm<z.infer<typeof applicationSelectLenderFormSchema>>({
    resolver: zodResolver(applicationSelectLenderFormSchema),
    defaultValues: {
      lender: application?.lender,
    },
  })

  const { data: lenders, isLoading: isLendersLoading } =
    useCustomerLendersAvailableList(companyUuid, {
      rId: fundingNeed.requestId,
    })

  const { data: products, isLoading: isProductsLoading } =
    useCommonProductsList()

  const productOptions =
    products?.map((product: Product) => ({
      label: product.name,
      value: product.id.toString(),
    })) || []

  const { mutate: createSelectLender, isPending: isSelectingLender } =
    useCustomerApplicationsSelectLenderCreate({
      mutation: {
        onSuccess: async (application) => {
          queryClient.setQueryData(
            getCustomerApplicationsRetrieveQueryKey(
              companyUuid,
              application.uuid,
            ),
            application,
          )
          queryClient.setQueryData(
            getCustomerFundingNeedsRetrieveQueryKey(
              companyUuid,
              application.fundingNeeds.uuid,
            ),
            application.fundingNeeds,
          )

          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getCustomerLendersRetrieveQueryKey(
                companyUuid,
                application.lender,
              ),
            }),
            queryClient.invalidateQueries({
              queryKey: getCustomerCompanyRetrieveQueryKey(companyUuid),
            }),
          ])
          flowActorRef.send({
            type: "setApplication",
            application,
          })
          flowActorRef.send({ type: "next" })
        },
        onError: (error: ErrorType<unknown>) => {
          applyErrorsToForm(
            form.setError,
            error as ErrorType<ErrorResponseType>,
          )
        },
      },
    })

  const handleSubmit = (
    data: z.infer<typeof applicationSelectLenderFormSchema>,
  ) => {
    if (!fundingNeed.requestId) {
      console.error("RequestId is missing")
      return
    }
    createSelectLender({
      companyUuid: companyUuid,
      data: { ...data, fundingNeedsUuid: fundingNeed.uuid },
    })
  }

  const capitalAmount = filterForm.watch("capitalAmount")

  const productOptionsSelected =
    (filterForm.watch("productOptions") as Record<string, string>[]).map(
      (p) => p.value,
    ) || []

  const activeFiltersCount =
    (capitalAmount ? 1 : 0) + (productOptionsSelected.length > 0 ? 1 : 0)

  const filteredLenders = lenders
    ?.filter((lender: LenderProfile) => {
      const capitalAmountMatches =
        !capitalAmount ||
        (lender.fundingAmountMin <= capitalAmount &&
          lender.fundingAmountMax >= capitalAmount)

      const productOptionsMatches =
        productOptionsSelected.length === 0 ||
        productOptionsSelected.some((value) =>
          lender.products.includes(parseInt(value)),
        )

      return capitalAmountMatches && productOptionsMatches
    })
    .sort((lenderA, lenderB) => {
      if (lenderA.isDealDesk && !lenderB.isDealDesk) return -1
      if (!lenderA.isDealDesk && lenderB.isDealDesk) return 1
      return 0
    })

  useEffect(() => {
    form.reset({
      lender: application?.lender,
    })
  }, [application, form])

  if (isLendersLoading || isProductsLoading) {
    return (
      <div className="form-card">
        <FormHeader title="Lender Match" />
        <div className="p-6">
          <p className="text-gray-500">Loading lenders...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3">
      <Card className="col-span-1 gap-2 p-6 lg:col-span-2 lg:gap-6">
        <FormHeader title="Lender Match" />
        <Form {...filterForm}>
          <form className="w-full">
            <div className="@container/form">
              <div className="hidden lg:block">
                <FormSection>
                  <FormInputNumeric
                    name="capitalAmount"
                    label="Funding Amount"
                    placeholder="Funding Amount"
                  />
                  <FormMultiselect
                    name="productOptions"
                    label="Select Product"
                    placeholder="Select Product"
                    options={productOptions}
                  />
                </FormSection>
              </div>

              <div className="lg:hidden">
                <Collapsible open={filtersOpen} onOpenChange={setFiltersOpen}>
                  <CollapsibleTrigger asChild>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground mb-2 flex items-center gap-2 text-sm font-medium transition"
                    >
                      Filters
                      {activeFiltersCount > 0 && (
                        <span className="text-muted-foreground text-xs">
                          ({activeFiltersCount})
                        </span>
                      )}
                      <ChevronDown className="h-4 w-4 transition-transform data-[state=open]:rotate-180" />
                    </button>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="rounded-md">
                      <FormSection>
                        <FormInputNumeric
                          name="capitalAmount"
                          label="Funding Amount"
                          placeholder="Funding Amount"
                        />
                        <FormMultiselect
                          name="productOptions"
                          label="Select Product"
                          placeholder="Select Product"
                          options={productOptions}
                        />
                      </FormSection>
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              </div>
            </div>
          </form>
        </Form>

        <Form {...form}>
          <form className="w-full" onSubmit={form.handleSubmit(handleSubmit)}>
            <h2 className="text-foreground mb-4 block text-base font-medium">
              Select a Lender:
            </h2>
            {filteredLenders?.length ? (
              <div className="max-h-lender-list overflow-y-auto pr-2">
                <LenderMatchForm
                  lenders={filteredLenders}
                  products={products}
                />
              </div>
            ) : (
              <BookACallCard />
            )}
            <div className="mt-8 flex flex-col gap-2 md:flex-row md:justify-end">
              <ButtonBack onClick={() => flowActorRef.send({ type: "back" })} />
              <ButtonNext
                type="submit"
                disabled={isSelectingLender}
                isLoading={isSelectingLender}
              />
            </div>
          </form>
        </Form>
      </Card>
    </div>
  )
}
